import {
  gaqlerrCannotCallFOOonBARTYPEvalue as errCannotCallFOOonBARTYPEvalue
} from '../gaqlerr.js'

import {
  gaqlchainIsShallow as chainIsShallow
} from '../gaqlchain.js'

import {
  gaqlasyncarrEvery as asyncarrEvery
} from '../gaqlasyncarr.js'

import {
  gaqlutilQueryArgsOptGet as utilQueryArgsOptGet,
  gaqlutilNumBoolOrStrIs as NumBoolOrStrIs
} from '../gaqlutil.js'

// Sum two or more numbers, or concatenate two or more strings or arrays.
//
// The add command can be called in either prefix or infix form; both
// forms are equivalent.
// ```
// value.add(value[, value, ...]) → value
// time.add(number[, number, ...]) → time
// q.add(q.args(vals)) -> number
// ```
const $add = async (gst, qst, args, run) => {
  const target = qst.target
  const values = await run(gst, qst, args)

  if (target === null) {
    qst.target = Array.isArray(values)
      ? values.slice(1).reduce((prev, val) => prev + val, values[0])
      : values
  } else if (NumBoolOrStrIs(target)) {
    qst.target = values.reduce((prev, val) => prev + val, target)
  } else if (Array.isArray(target)) {
    // args is always a list and when query atom returns list
    // there is list of list, so take care to reduce these
    if (chainIsShallow(args)) {
      qst.target = values.reduce((acc, val) => (
        acc = acc.concat(val),
        acc
      ), qst.target.slice())
    } else {
      qst.target = target.slice().concat(values)
    }
  }

  return qst
}

// Subtract two numbers.
// ```
// number.sub(number[, number, ...]) → number
// time.sub(number[, number, ...]) → time
// time.sub(time) → number
// q.sub(q.args(vals)) -> number
// ```
const $sub = async (gst, qst, args, run) => {
  const target = qst.target
  const values = await run(gst, qst, args)

  if (target === null) {
    qst.target = Array.isArray(values)
      ? values.slice(1).reduce((prev, val) => prev - val, values[0])
      : values
  } else if (NumBoolOrStrIs(target)) {
    qst.target = values.reduce((prev, val) => prev - val, target)
  }

  return qst
}

// Multiply two numbers, or make a periodic array.
// ```
// number.mul(number[, number, ...]) → number
// array.mul(number[, number, ...]) → array
// ```
const $mul = async (gst, qst, args, run) => {
  const target = qst.target
  const values = await run(gst, qst, args)

  if (target === null) {
    qst.target = Array.isArray(values)
      ? values.slice(1).reduce((prev, val) => prev * val, values[0])
      : values
  } else if (NumBoolOrStrIs(target)) {
    qst.target = values.reduce((prev, val) => prev * val, target)
  }

  return qst
}

// Divide two numbers, or make a periodic array.
// ```
// number.div(number[, number ...]) → number
// array.div(number[, number, ...]) → array
// ```
const $div = async (gst, qst, args, run) => {
  const target = qst.target
  const values = await run(gst, qst, args)

  if (target === null) {
    qst.target = Array.isArray(values)
      ? values.slice(1).reduce((prev, val) => prev / val, values[0])
      : values
  } else if (NumBoolOrStrIs(target)) {
    qst.target = values.reduce((prev, val) => prev / val, target)
  }

  return qst
}

// Compute the logical “and” of one or more values.
//
// The and command can be used as an infix operator after its first argument
//
// Calling and with zero arguments will return true.
// ```
// bool.and([bool, bool, ...]) → bool
// q.and([bool, bool, ...]) → bool
// ```
const $and = async (gst, qst, args, run) => {
  const target = qst.target
  const rows = [target]

  // qst.target = await (async and => {
  //   while (and && args.length)
  // })(typeof target === 'boolean' ? target : true)
  const start = typeof target === 'boolean' ? target : true
  qst.target = start && await asyncarrEvery(args, async arg => (
    run(gst, qst, arg, rows)
  ))

  return qst
}

// Compute the logical “or” of one or more values.
//
// The or command can be used as an infix operator after its first argument
// (q.expr(true).or(false)) or given all of its arguments as parameters
// (q.or(true,false)).
//
// Calling or with zero arguments will return false.
// ```
// bool.or([bool, bool, ...]) → bool
// q.or([bool, bool, ...]) → bool
// ```
const $or = async (gst, qst, args, run) => {
  const rows = [qst.target]

  while (!qst.target && args.length) {
    qst.target = await run(gst, qst, args[0], rows)
    args = args.slice(1)
  }

  return qst
}

// Test if two or more values are equal.
// ```
// q(a).eq(c)
// q.eq(a, b, c)
// ```
const $eq = async (gst, qst, args, run) => {
  const target = qst.target
  const start = await run(gst, qst, args[0])

  if (args.length === 1) {
    qst.target = target === start
  } else {
    qst.target = await asyncarrEvery(args.slice(1), async arg => (
      start === await run(gst, qst, arg)
    ))
  }

  return qst
}

// Test if two or more values are not equal.
//
// ```
// value.ne(value[, value, ...]) → bool
// ```
const $ne = async (gst, qst, args, run) => {
  qst.target = !(await $eq(gst, qst, args, run)).target

  return qst
}

// Compare values, testing if the left-hand value is greater than the
// right-hand.
// ```
// value.gt(value[, value, ...]) → bool
// ```
const $gt = async (gst, qst, args, run) => {
  qst.target = qst.target > await run(gst, qst, args[0])

  return qst
}

// Compare values, testing if the left-hand value is greater than or equal to
// the right-hand.
// ```
// value.ge(value[, value, ...]) → bool
// ```
const $ge = async (gst, qst, args, run) => {
  qst.target = qst.target >= await run(gst, qst, args[0])

  return qst
}

// Compare values, testing if the left-hand value is less than the right-hand.
// ```
// value.lt(value[, value, ...]) → bool
// ```
const $lt = async (gst, qst, args, run) => {
  const argTarget = await run(gst, qst, args[0])

  if (typeof qst.target === typeof qst.target) {
    qst.target = qst.target < argTarget
  }

  return qst
}

// Compare values, testing if the left-hand value is less than or equal to the
// right-hand.
// ```
// value.le(value[, value, ...]) → bool
// ```
const $le = async (gst, qst, args, run) => {
  qst.target = qst.target <= await run(gst, qst, args[0])

  return qst
}

// Compute the logical inverse (not) of an expression.
//
// not can be called either via method chaining, immediately after an expression
// that evaluates as a boolean value, or by passing the expression as a
// parameter to not. All values that are not false or null will be converted
// to true.
// ```
// q(true).not() // `false`
// q.not(true) // `false`
// ```
const $not = async (gst, qst, args, run) => {
  const target = args.length
    ? await run(gst, qst, args[0])
    : qst.target

  if (typeof target !== 'boolean')
    throw errCannotCallFOOonBARTYPEvalue('not()', typeof target)

  qst.target = !target

  return qst
}

// Generate a random number between given (or implied) bounds. random takes
// zero, one or two arguments.
//  * With zero arguments, the result will be a floating-point number in the
//    range [0,1) (from 0 up to but not including 1).
//  * With one argument x, the result will be in the range [0,x), and will be
//    integer unless {float: true} is given as an option. Specifying a floating
//    point number without the float option will raise an error.
//  * With two arguments x and y, the result will be in the range [x,y), and
//    will be integer unless {float: true} is given as an option. If x and y
//    are equal an error will occur, unless the floating-point option has been
//    specified, in which case x will be returned. Specifying a floating point
//    number without the float option will raise an error.
//
// Note: The last argument given will always be the ‘open’ side of the range,
// but when generating a floating-point number, the ‘open’ side may be less
// than the ‘closed’ side.
// ```
// r.random() → number
// r.random(number[, number], {float: true}) → number
// r.random(integer[, integer]) → integer
// ```
const $random = async (gst, qst, args) => {
  const opts = utilQueryArgsOptGet(args, null)
  const nums = opts ? args.slice(0, -1) : args
  const numslength = nums.length

  if (numslength === 0) {
    qst.target = Math.random()
  } else {
    const open = numslength > 1 ? nums[1] : nums[0]
    const closed = (numslength > 1 ? nums[0] : 0) + 1

    qst.target = Math.floor(Math.random() * (open - closed)) + closed
  }

  return qst
}

// Rounds the given value to the nearest whole integer.
//
// For example, values of 1.0 up to but not including 1.5 will return 1.0,
// similar to floor; values of 1.5 up to 2.0 will return 2.0, similar to ceil.
// ```
// r.round(number) → number
// number.round() → number
// ```
const $round = async (gst, qst, args, run) => {
  const opts = utilQueryArgsOptGet(args)
  let target = qst.target

  if (typeof target !== 'number' && args.length) {
    target = typeof args[0] === 'number'
      ? args[0] : await run(gst, qst, args[0])

    if (typeof target !== 'number')
      throw errCannotCallFOOonBARTYPEvalue('round()', typeof target)
  }

  qst.target = (opts && opts.digits)
    ? +Number.parseFloat(target).toFixed(opts.digits)
    : Math.round(target)

  return qst
}

// Rounds the given value up, returning the smallest integer value greater than
// or equal to the given value (the value’s ceiling).
// ```
// r.ceil(number) → number
// number.ceil() → number
// ```
const $ceil = async (gst, qst, args, run) => {
  let target = qst.target

  if (typeof target !== 'number' && args.length) {
    target = typeof args[0] === 'number'
      ? args[0] : await run(gst, qst, args[0])

    if (typeof target !== 'number')
      throw errCannotCallFOOonBARTYPEvalue('ceil()', typeof target)
  }

  qst.target = Math.ceil(target)

  return qst
}

// Rounds the given value down, returning the largest integer value less than or
// equal to the given value (the value’s floor).
// ```
// r.floor(number) → number
// number.floor() → number
// ```
const $floor = async (gst, qst, args, run) => {
  let target = qst.target

  if (typeof target !== 'number' && args.length) {
    target = typeof args[0] === 'number'
      ? args[0] : await run(gst, qst, args[0])

    if (typeof target !== 'number')
      throw errCannotCallFOOonBARTYPEvalue('floor()', typeof target)
  }

  qst.target = Math.floor(target)

  return qst
}

export {
  $random,
  $round,
  $ceil,
  $floor,
  $add,
  $sub,
  $mul,
  $div,
  $and,
  $or,
  $eq,
  $ne,
  $gt,
  $ge,
  $lt,
  $le,
  $not
}
